import React, { useCallback, useContext, useMemo } from "react";
import { EventCards, LeadCardContactButton } from "src/Components/Segments/DashboardSideBarSegments/LeadCardV2";
import { LeadCardTopInfoV2 } from "src/Components/Segments/DashboardSideBarSegments/LeadCardV2/LeadCardTopInfoV2";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixIcon } from "src/Components/UI/Phoenix";
import { CallContext, ModalContext } from "src/context";
import { chevron_down, chevron_up, contact_info_add, list, merge_primary, user_add } from "src/images/NewDesign";
import { MixpanelActions } from "src/services/mixpanel";
import { returnLocalTime } from "src/static";
import { useDisableActionButton, useLocalStoragePersistedState } from "src/utils/hooks";
import { ON_DIAL_WARNING_TOOLTIP } from "src/utils/misc";
import { theme } from "src/utils/theme";
import { appToast } from "src/utils/toast";
import styled from "styled-components";
import { AssociatedContact, ContactModule } from "../DashboardV2/DetailedLeadCard/ContactTab";
import { Field, FieldWrapper } from "../DashboardV2/DetailedLeadCard/MoreInfoTab";
import RelatedRecordCardData from "../LeadDetail/CustomObjectRelatedRecordData";

interface ILeadDataColumn {
  leadData: any;
  associatedContacts: any[];
  dataCustomObjectSummary: any;
}

export const LeadDataColumn: React.FC<ILeadDataColumn> = ({
  leadData,
  associatedContacts,
  dataCustomObjectSummary,
}) => {
  const { goToCall, callState, dialAsNumber, resetNextDial, setSelectedLeadId } = useContext(CallContext);
  const { openSMSChat, showSMSChat, showEmailEditorModal, openEmailEditorModal } = useContext(ModalContext);

  const goToCallObjectForPrimary = useMemo(
    () => ({
      lead_id: leadData?.id,
      intentId: leadData?.next_intent_scheduled_or_unscheduled?.id
        ? leadData?.next_intent_scheduled_or_unscheduled?.id
        : undefined,
      intentReplacementId: leadData?.next_intent_scheduled_or_unscheduled?.replacement_lead_intent_id
        ? leadData?.next_intent_scheduled_or_unscheduled?.replacement_lead_intent_id
        : undefined,
      dialAsNumber: dialAsNumber,
    }),
    [leadData, dialAsNumber],
  );

  const fields = useMemo(() => {
    return leadData?.visible_custom_fields?.filter((field: any) => field?.computed_value) || [];
  }, [leadData]);

  const actionDisabled = useDisableActionButton(leadData);

  return (
    <Container>
      {leadData && (
        <>
          <QuickActionsContainer>
            <div
              data-tip={!!callState ? ON_DIAL_WARNING_TOOLTIP : ""}
              data-for={!!callState ? "lead-card-tooltip" : ""}
            >
              <LeadCardContactButton
                disabled={actionDisabled["manualCall"]}
                callState={callState}
                onClick={() => {
                  goToCall({
                    phoneNumber: leadData?.primary_phone_number,
                    ...goToCallObjectForPrimary,
                  });
                  resetNextDial();
                  leadData?.id && setSelectedLeadId(leadData.id);
                }}
                type="phone"
                primaryButton
              />
            </div>
            <LeadCardContactButton
              disabled={actionDisabled["manualSms"]}
              onClick={() => {
                if (showSMSChat) {
                  appToast("There is currently a chat open. Please close the existing chat before opening.");
                  return;
                }
                MixpanelActions.track("Lead Card", {
                  type: "sms clicked",
                  id: leadData?.id,
                });

                openSMSChat({
                  lead_id: leadData?.id ?? "",
                  phone_number: leadData?.primary_phone_number,
                  intent: leadData?.next_intent_scheduled_or_unscheduled,
                });
              }}
              type="sms"
              primaryButton
            />
            <LeadCardContactButton
              disabled={actionDisabled["manualEmail"]}
              onClick={() => {
                MixpanelActions.track("Lead Card", {
                  type: "email clicked (not primary)",
                  id: leadData?.id,
                });
                !showEmailEditorModal &&
                  openEmailEditorModal({
                    lead_id: leadData?.id ?? "",
                    full_name: leadData.full_name,
                    email: leadData?.primary_email,
                    isManualEmailSequenceStep: leadData?.sequence_step?.actions?.[0]?.task === "manualEmail",
                    sequenceStepId: leadData?.sequence_step?.actions?.[0]?.id,
                  });
              }}
              type="email"
              width={32}
              height={32}
              primaryButton
            />
          </QuickActionsContainer>
          <LeadDataWrap>
            <LeadCardTopInfoV2
              computedLeadData={leadData as any}
              customFieldData={leadData?.visible_custom_fields || []}
              currentTime={returnLocalTime(leadData?.timezone_by_state)}
            />
            <FlexDiv
              padding="8px 0px 8px 0px"
              direction="column"
              gap={8}
              style={{ minHeight: "34px", maxHeight: "84px" }}
            >
              <EventCards lead={leadData} showEventTypeCard computedLeadData={leadData} />
            </FlexDiv>
          </LeadDataWrap>

          <Drawer type="Contact" leadData={leadData} />
          <Drawer type="Associated Contacts" leadData={leadData} associatedContacts={associatedContacts} />
          <Drawer type="More Info" leadData={leadData} fields={fields} />
          <Drawer type="Related Records" leadData={leadData} dataCustomObjectSummary={dataCustomObjectSummary} />
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 500px;
  min-width: 500px;
  @media (max-width: 1568px) {
    width: 424px;
    min-width: 424px;
  }

  height: 100%;
  max-height: calc(100vh - 104px);

  overflow-y: auto;

  padding-bottom: 64px;

  background-color: ${theme.fill.neutral.primary};
  border-right: 1px solid ${theme.border.neutral.primary};
`;

const LeadDataWrap = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const QuickActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;

  padding: 16px 0px;

  border-bottom: 1px solid ${theme.border.neutral.secondary};
  background-color: ${theme.fill.brand.tertiary};
`;

interface IDrawer {
  type: "Contact" | "Associated Contacts" | "More Info" | "Related Records";
  leadData?: any;
  associatedContacts?: any;
  fields?: any[];
  dataCustomObjectSummary?: any;
}

const Drawer: React.FC<IDrawer> = ({ type, leadData, associatedContacts, fields, dataCustomObjectSummary }) => {
  const {
    setShowAddOrEditContactModal,
    setSelectedContactV2,
    setShowAddAssociateModal,
    setPrimaryLeadToAddAssociateTo,
    setShowMergeContactsModal,
    setMergeLeadsData,
    setCustomFieldsData,
    openCustomFieldsModal,
  } = useContext(ModalContext);

  const [expanded, setExpanded] = useLocalStoragePersistedState<boolean>(`drawer-${type}-expanded`, false, {
    onGet: (persistedValue: string) => persistedValue === "true",
  });

  const handleAddContact = useCallback(() => {
    setShowAddOrEditContactModal(true);
    setSelectedContactV2({
      lead_id: leadData?.id ?? "",
      country_code: leadData?.primary_phone_number_country_code,
      card_type: "Add",
      // if the lead already has a conference number they can't add another
      has_conference_number: !!leadData?.conference_number,
    });
  }, [leadData]);
  const handleShowAddNewAssociateModal = useCallback(() => {
    setShowAddAssociateModal(true);
    setPrimaryLeadToAddAssociateTo(leadData?.id);
  }, [leadData]);

  const handleShowAssociateExistingModal = useCallback(() => {
    setShowMergeContactsModal(true);
    setMergeLeadsData({
      source: {
        id: leadData?.id,
        full_name: leadData?.full_name,
        business_name: leadData?.business_name,
      },
    });
  }, [leadData]);

  return (
    <DrawerContainer>
      <FlexDiv justify="space-between">
        <FlexDiv direction="column" gap={8}>
          <AppText fontSize={14} fontWeight={500} lineHeight={20}>
            {type}
          </AppText>
          {type === "Contact" && (
            <FlexDiv align="center" gap={4} style={{ cursor: "pointer" }} onClick={handleAddContact}>
              <PhoenixIcon
                svg={contact_info_add}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
                pointer
              />
              <AppText
                fontSize={10}
                fontWeight={600}
                lineHeight={16}
                uppercase
                letterSpacing={1}
                color={theme.text.brand.primary}
              >
                Add Contact Info
              </AppText>
            </FlexDiv>
          )}
          {type === "Associated Contacts" && (
            <FlexDiv align="center" gap={8}>
              <FlexDiv align="center" gap={4} style={{ cursor: "pointer" }} onClick={handleShowAddNewAssociateModal}>
                <PhoenixIcon
                  svg={user_add}
                  size={16}
                  color={theme.icon.brand.default}
                  hoverColor={theme.icon.brand.default}
                  pointer
                />
                <AppText
                  fontSize={10}
                  fontWeight={600}
                  lineHeight={16}
                  uppercase
                  letterSpacing={1}
                  color={theme.text.brand.primary}
                >
                  Add New
                </AppText>
              </FlexDiv>
              <FlexDiv align="center" gap={4} style={{ cursor: "pointer" }} onClick={handleShowAssociateExistingModal}>
                <PhoenixIcon
                  svg={merge_primary}
                  size={16}
                  color={theme.icon.brand.default}
                  hoverColor={theme.icon.brand.default}
                  pointer
                />
                <AppText
                  fontSize={10}
                  fontWeight={600}
                  lineHeight={16}
                  uppercase
                  letterSpacing={1}
                  color={theme.text.brand.primary}
                >
                  Associate Existing
                </AppText>
              </FlexDiv>
            </FlexDiv>
          )}
          {type === "More Info" && (
            <FlexDiv
              align="center"
              gap={4}
              style={{ cursor: "pointer" }}
              onClick={() => {
                setCustomFieldsData({
                  lead: {
                    id: leadData?.id,
                    visible_custom_fields: leadData?.visible_custom_fields,
                  },
                });
                openCustomFieldsModal();
              }}
            >
              <AppText
                fontSize={10}
                fontWeight={600}
                letterSpacing={1}
                uppercase
                color={theme.buttontext.brand_outline.default}
              >
                Edit More Info
              </AppText>
            </FlexDiv>
          )}
        </FlexDiv>
        <PhoenixIcon
          svg={expanded ? chevron_up : chevron_down}
          size={24}
          pointer
          onClick={() => setExpanded(!expanded)}
        />
      </FlexDiv>

      {expanded && type === "Contact" && (
        <FlexDiv direction="column" gap={16} padding="16px 0px 0px 0px">
          <ContactModule type="Phone" leadData={leadData} />
          <ContactModule type="Email" leadData={leadData} />
          <ContactModule type="Screen Share" leadData={leadData} />
        </FlexDiv>
      )}
      {expanded &&
        type === "Associated Contacts" &&
        (associatedContacts?.length ? (
          <FlexDiv direction="column" gap={16} padding="16px 0px 0px 0px">
            {associatedContacts?.map((ac: any) => (
              <AssociatedContact data={ac} key={ac.id} />
            ))}
          </FlexDiv>
        ) : (
          <AppText
            fontSize={10}
            fontWeight={500}
            uppercase
            color={theme.text.neutral.secondary}
            style={{ paddingTop: "16px" }}
          >
            No Associated Contacts Found
          </AppText>
        ))}
      {expanded &&
        type === "More Info" &&
        (!fields?.length ? (
          <FlexDiv direction="column" gap={24} align="center" padding="12px 0px 0px 0px">
            <PhoenixIcon
              svg={list}
              size={40}
              color={theme.icon.neutral.default}
              hoverColor={theme.icon.neutral.default}
            />
            <AppText fontSize={14} fontWeight={400} lineHeight={22} color={theme.text.neutral.tertiary}>
              No custom data for this lead
            </AppText>
          </FlexDiv>
        ) : (
          <div style={{ paddingTop: "16px" }}>
            <FieldWrapper>
              {fields?.map((field: any, index: number) => {
                const fieldValue = field?.computed_value;
                const value = fieldValue || field?.value;
                const fullWidth = fields.length % 2 !== 0 && index === fields.length - 1;

                return (
                  <Field key={`${leadData?.id}-${field.key}`} label={field.key} value={value} fullWidth={fullWidth} />
                );
              })}
            </FieldWrapper>
          </div>
        ))}
      {expanded && type === "Related Records" && (
        <div style={{ paddingTop: "16px" }}>
          <RelatedRecordCardData
            lead_id={leadData?.id}
            customObjectSummary={dataCustomObjectSummary?.fetchAssociatedCustomObjects}
          />
        </div>
      )}
    </DrawerContainer>
  );
};

const DrawerContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;
